/**
 * Notify Me App DOM 兼容 —— 吸底栏代理按钮
 *
 * App 会把 `.nm-portal` 注入并持续校正到 `.product-form__buttons`。
 * 若再把它挪到吸底栏,会与 App 来回抢节点闪烁。
 *
 * 策略:
 * 1. 最多把误注入的 portal 挪回商详 `.product-form__buttons`
 * 2. 吸底栏用代理按钮展示,点击时实时查找 App 原按钮并转发
 * 3. 显隐以吸底「缺货/不可买」为准,不跟 App 在隐藏表单里的 display 抖动
 *    (变体 morph 时 App 会短暂移除/隐藏按钮,若跟 display 会先出现再消失)
 */
const STICKY_MOUNT_SELECTOR = '[data-fanka-notify-me-sticky-mount]';
const PRODUCT_FORM_SELECTOR = '.product-details .buy-buttons-block .product-form__buttons';
const PORTAL_SELECTOR = '.nm-portal';
const PAGE_BUTTON_SELECTOR = '.PRODUCT_PAGE-notify-button, .restock-alerts-notify-button';
const PROXY_SELECTOR = '[data-fanka-notify-me-proxy]';
const RETRY_DELAYS_MS = [80, 200, 400, 800, 1600];

/** @type {number | null} */
let scheduled = null;

/** @type {number[]} */
let retryTimers = [];

/** 自身改 DOM 时跳过 observer */
let syncing = false;

function getStickyMount() {
  return document.querySelector(`sticky-add-to-cart ${STICKY_MOUNT_SELECTOR}`);
}

function getProductFormButtons() {
  return document.querySelector(PRODUCT_FORM_SELECTOR);
}

/** 吸底加购不可用 = 当前变体缺货/无效组合 */
function isStickyUnavailable() {
  const stickyButton = document.querySelector('sticky-add-to-cart [ref="addToCartButton"]');
  if (!(stickyButton instanceof HTMLButtonElement)) return false;
  return stickyButton.disabled;
}

/**
 * 若 App 误注入到其它 form,挪回商详 product-form__buttons。
 * 不要挂到吸底栏,否则会与 App 抢节点。
 */
function ensurePortalInProductForm() {
  const home = getProductFormButtons();
  if (!home) return null;

  document.querySelectorAll(PORTAL_SELECTOR).forEach((portal) => {
    if (!(portal instanceof HTMLElement)) return;
    if (!portal.querySelector(PAGE_BUTTON_SELECTOR)) return;
    if (home.contains(portal)) return;

    home.appendChild(portal);
  });

  return home.querySelector(PAGE_BUTTON_SELECTOR);
}

function getAppNotifyButton() {
  return (
    ensurePortalInProductForm() ||
    document.querySelector(`.product-details ${PAGE_BUTTON_SELECTOR}`) ||
    document.querySelector(PAGE_BUTTON_SELECTOR)
  );
}

function createProxyButton() {
  const proxy = document.createElement('button');
  proxy.type = 'button';
  proxy.className = 'fanka-notify-me-proxy';
  proxy.dataset.fankaNotifyMeProxy = '';
  proxy.setAttribute('aria-label', 'Notify Me');

  const label = document.createElement('span');
  label.className = 'fanka-notify-me-proxy__label';
  label.textContent = 'Notify Me';
  proxy.appendChild(label);

  proxy.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    // 点击时再取原按钮,避免 morph 后持有失效节点
    getAppNotifyButton()?.click();
  });

  return proxy;
}

/**
 * @param {HTMLElement} mount
 * @param {HTMLElement | null} original
 */
function syncProxyButton(mount, original) {
  let proxy = mount.querySelector(PROXY_SELECTOR);

  if (!(proxy instanceof HTMLElement)) {
    proxy = createProxyButton();
    mount.replaceChildren(proxy);
  }

  const label = proxy.querySelector('.fanka-notify-me-proxy__label');
  const nextText = original?.textContent?.replace(/\s+/g, ' ').trim() || 'Notify Me';
  if (label && label.textContent !== nextText) {
    label.textContent = nextText;
  }
  proxy.setAttribute('aria-label', nextText);

  // 图标随 App 按钮更新(若有)
  const nextIcon = original?.querySelector('svg');
  const currentIcon = proxy.querySelector('svg');
  if (nextIcon && !currentIcon) {
    proxy.insertBefore(nextIcon.cloneNode(true), label);
  } else if (nextIcon && currentIcon) {
    currentIcon.replaceWith(nextIcon.cloneNode(true));
  }
}

function clearRetryTimers() {
  retryTimers.forEach((id) => window.clearTimeout(id));
  retryTimers = [];
}

function scheduleRetry() {
  clearRetryTimers();
  retryTimers = RETRY_DELAYS_MS.map((delay) =>
    window.setTimeout(() => {
      syncStickyNotifyProxy();
    }, delay)
  );
}

function syncStickyNotifyProxy() {
  const mount = getStickyMount();
  if (!mount) return;

  syncing = true;
  try {
    const unavailable = isStickyUnavailable();
    const original = getAppNotifyButton();

    // 有货:收起代理
    if (!unavailable) {
      if (!mount.hidden) mount.hidden = true;
      if (mount.childNodes.length) mount.replaceChildren();
      clearRetryTimers();
      return;
    }

    // 缺货:展示代理。不因 App 短暂卸载/display:none 而清掉已有按钮
    if (original || mount.querySelector(PROXY_SELECTOR)) {
      syncProxyButton(mount, original instanceof HTMLElement ? original : null);
      if (mount.hidden) mount.hidden = false;
      // App 按钮还没到时继续短重试,到了就停
      if (!original) scheduleRetry();
      else clearRetryTimers();
      return;
    }

    // 缺货但 App 与代理都还没有:隐藏并重试等待注入
    if (!mount.hidden) mount.hidden = true;
    scheduleRetry();
  } finally {
    syncing = false;
  }
}

function scheduleSync() {
  if (scheduled != null) return;
  scheduled = window.requestAnimationFrame(() => {
    scheduled = null;
    syncStickyNotifyProxy();
  });
}

/**
 * @param {Node} node
 */
function isNotifyRelatedNode(node) {
  if (!(node instanceof Element)) return false;
  return Boolean(
    node.matches(PORTAL_SELECTOR) ||
      node.matches(PAGE_BUTTON_SELECTOR) ||
      node.matches('sticky-add-to-cart') ||
      node.querySelector(PORTAL_SELECTOR) ||
      node.querySelector(PAGE_BUTTON_SELECTOR) ||
      node.querySelector('sticky-add-to-cart')
  );
}

/**
 * @param {MutationRecord[]} mutations
 */
function shouldSync(mutations) {
  return mutations.some((mutation) => {
    if (mutation.type === 'childList') {
      if (mutation.target instanceof Element && mutation.target.closest(STICKY_MOUNT_SELECTOR)) {
        return false;
      }
      // 吸底栏 morph / App 注入都要同步
      if (mutation.target instanceof Element && mutation.target.closest('sticky-add-to-cart')) {
        return true;
      }
      return [...mutation.addedNodes, ...mutation.removedNodes].some(isNotifyRelatedNode);
    }

    if (mutation.type !== 'attributes') return false;

    const target = mutation.target;
    if (!(target instanceof HTMLElement)) return false;
    if (target.matches(STICKY_MOUNT_SELECTOR) || target.closest(STICKY_MOUNT_SELECTOR)) return false;

    // 吸底加购 disabled / data-variant-available 变化
    if (target.matches('sticky-add-to-cart') || target.matches('[ref="addToCartButton"]')) {
      return mutation.attributeName === 'disabled' || mutation.attributeName === 'data-variant-available';
    }

    if (!target.matches(PORTAL_SELECTOR) && !target.matches(PAGE_BUTTON_SELECTOR)) return false;
    return mutation.attributeName === 'hidden' || mutation.attributeName === 'style';
  });
}

syncStickyNotifyProxy();

new MutationObserver((mutations) => {
  if (syncing) return;
  if (!shouldSync(mutations)) return;
  scheduleSync();
}).observe(document.documentElement, {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ['style', 'hidden', 'disabled', 'data-variant-available'],
});

// 变体切换后再撑一次,覆盖 morph 时序空洞(事件名随 Shopify events bundle)
for (const eventName of ['product:selected', 'variant:selected', 'product-select']) {
  document.addEventListener(
    eventName,
    () => {
      scheduleSync();
      scheduleRetry();
    },
    { passive: true, capture: true }
  );
}
